/**
 * Delegation (core delegate.ts; docs: next.ts N6):
 *  - AssignLine: the one line on a task card, "Dana asked you." or "Susan's.", the same words on web and phone.
 *  - useStepIn: "I'll do it" on a task that is someone else's. It is recorded; it never blocks the work.
 *  - AssignSheet: a manager or the person in charge gives a task to a person or a role on shift, just this once or every time.
 *    One question per screen: who, then how often, then one button. Taking it back is one quiet button.
 */
import { useCallback, useState } from "react";
import { DELEGATE_WORDS, SUGGEST_WORDS, firstName, type AssignScope, type CarryingResponse } from "@logwave/core";
import { saveErrorText } from "../lib/failures.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, Chips, Notice, Sheet, useToast } from "./ui.tsx";

/** "Dana asked you." / "Susan's.": who asked, or whose it is. Under a task's title on the Next card and the deck. */
export function AssignLine({ assign }: { assign: { mine: boolean; line: string; seen?: string } }) {
  return (
    <>
      <p className="assign-line" data-mine={assign.mine} data-testid="assign-line">
        <Icon name={assign.mine ? "handoff" : "people"} size={16} />{assign.line}
      </p>
      {assign.seen && <p className="small assign-seen" data-testid="assign-seen">{assign.seen}</p>}
    </>
  );
}

/** "I'll do it": records the step-in, then lets the work go on. A failure to record is never in the way of the task. */
export function useStepIn() {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  return useCallback(async (itemKey: string) => {
    try {
      const r = await api.stepIn(s.org.id, { locationId: s.location.id, itemKey });
      if (r.recorded) toast.show(r.line, { icon: "check" });
    } catch { /* the record is a nicety here: the work still starts */ }
  }, [api, s.org.id, s.location.id, toast]);
}

type Choices = CarryingResponse["choices"];
type Who = { kind: "person"; id: string } | { kind: "role"; id: string };
const keyOf = (w: Who) => `${w.kind}:${w.id}`;

/** Give one task out. `item` is the occurrence on today's list; `current` says who has it now, so "Take it back" shows only then. */
export function AssignSheet({ item, choices, current, onClose, onDone }: { item: { key: string; title: string }; choices: Choices; current?: boolean; onClose: () => void; onDone?: () => void | Promise<void> }) {
  const api = useApi();
  const s = useSession();
  const [who, setWho] = useState<Who>();
  const [scope, setScope] = useState<AssignScope>("today");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const [done, setDone] = useState<string>();
  const [undoable, setUndoable] = useState(false);
  const suggested = choices.suggest?.[item.key] ?? [];
  // With suggestions, the full list waits behind a show-more button, in place. Without any, it is open as before.
  const [more, setMore] = useState(suggested.length === 0);
  const people = choices.people;
  const on = people.filter((p) => p.onShift), off = people.filter((p) => !p.onShift);

  async function send(to: { employeeId: string } | { role: string } | null, how: AssignScope = scope, quick = false) {
    setBusy(true); setErr(undefined);
    try {
      const r = await api.assign(s.org.id, { locationId: s.location.id, itemKey: item.key, to, scope: how });
      setDone(r.line); setUndoable(quick && to !== null);
      await onDone?.();
    } catch (x) { setErr(saveErrorText(x)); } finally { setBusy(false); }
  }

  /** Undo a one-tap give: back to anyone on shift, for today. */
  async function undo() {
    setBusy(true); setErr(undefined);
    try {
      await api.assign(s.org.id, { locationId: s.location.id, itemKey: item.key, to: null, scope: "today" });
      setDone(undefined); setUndoable(false);
      await onDone?.();
    } catch (x) { setErr(saveErrorText(x)); } finally { setBusy(false); }
  }

  return (
    <Sheet kicker={item.title} title={done ? "Done" : DELEGATE_WORDS.assign} onClose={onClose}>
      {done ? (
        <div className="stack" role="status" data-testid="assign-done">
          <Notice tone="go" icon="check" title="Given out">{done}</Notice>
          {err && <p className="field-msg" role="alert">{err}</p>}
          {undoable && <Button block variant="quiet" busy={busy} onClick={() => void undo()} data-testid="assign-undo">{SUGGEST_WORDS.undo}</Button>}
          <Button big block icon="check" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="stack" data-testid="assign-sheet">
          {suggested.length > 0 && (
            <div className="stack-sm" data-testid="assign-suggested">
              <p className="label">{SUGGEST_WORDS.suggested}</p>
              <div className="chips">
                {suggested.map((g) => (
                  <button key={g.employeeId} type="button" className="chip" disabled={busy} data-testid={`assign-suggest-${g.employeeId}`} onClick={() => void send({ employeeId: g.employeeId }, "today", true)}>
                    {firstName(g.name)}{g.employeeId === s.employeeId ? " · you" : ""}<span className="chip-sub"> · {g.why}</span>
                  </button>
                ))}
              </div>
              {!more && <Button block variant="quiet" onClick={() => setMore(true)} data-testid="assign-more">{SUGGEST_WORDS.showMore}</Button>}
            </div>
          )}
          {err && suggested.length > 0 && <p className="field-msg" role="alert">{err}</p>}
          {more && <>
          <div className="stack-sm">
            <p className="label" id="assign-who">Who?</p>
            <div className="chips" role="radiogroup" aria-labelledby="assign-who">
              {on.map((p) => <WhoChip key={p.employeeId} label={firstName(p.name)} sub={p.employeeId === s.employeeId ? "you" : undefined} on={who ? keyOf(who) === `person:${p.employeeId}` : false} pick={() => setWho({ kind: "person", id: p.employeeId })} />)}
              {choices.roles.map((r) => <WhoChip key={r.role} label={r.label} on={who ? keyOf(who) === `role:${r.role}` : false} pick={() => setWho({ kind: "role", id: r.role })} role />)}
              {off.map((p) => <WhoChip key={p.employeeId} label={firstName(p.name)} sub="not here yet" on={who ? keyOf(who) === `person:${p.employeeId}` : false} pick={() => setWho({ kind: "person", id: p.employeeId })} />)}
            </div>
            <p className="caption">A job or "whoever closes" goes to the people on shift it fits. The first to say "I'll do it" takes it.</p>
          </div>
          <Chips label="How often?" value={scope} onChange={setScope} options={[{ value: "today" as const, label: DELEGATE_WORDS.justThis }, { value: "standing" as const, label: DELEGATE_WORDS.everyTime }]} />
          {err && suggested.length === 0 && <p className="field-msg" role="alert">{err}</p>}
          <Button big block icon="handoff" busy={busy} disabled={!who} reason="Pick who first." onClick={() => void send(who!.kind === "person" ? { employeeId: who!.id } : { role: who!.id })}>{DELEGATE_WORDS.assign}</Button>
          </>}
          {current && <Button block variant="quiet" busy={busy} onClick={() => void send(null)}>Back to anyone on shift</Button>}
        </div>
      )}
    </Sheet>
  );
}

function WhoChip({ label, sub, on, pick, role }: { label: string; sub?: string; on: boolean; pick: () => void; role?: boolean }) {
  return (
    <button type="button" role="radio" aria-checked={on} className={`chip${on ? " chip-on" : ""}${role ? " chip-role" : ""}`} onClick={pick}>
      {role && <Icon name="people" size={16} />}{label}{sub ? <span className="chip-sub"> · {sub}</span> : null}
    </button>
  );
}
